// src/SavedPosts.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import './SavedPosts.css';

import Post from './Post';
import backIcon from './assets/backspace-icon.png';
import placeholderProfile from './assets/placeholder-profile.png';

import api from './api';

import { useSavedPosts } from './context/SavedPostsContext';
import { usePostContext } from './context/PostContext';
import { POST_VIEW_COUNT_UPDATED_EVENT } from './services/postViewTracker';

const BATCH_SIZE = 10;

function SavedPosts({ currentUser }) {
  const {
    savedPostIds,
    hasLoaded,
  } = useSavedPosts();

  const {
    getPostInteractions,
  } = usePostContext();

  const [
    displayedPosts,
    setDisplayedPosts,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    hasMore,
    setHasMore,
  ] = useState(true);

  const [
    currentIndex,
    setCurrentIndex,
  ] = useState(0);


  const feedRef = useRef(null);

  // Keep authoritative view counts synchronized in Saved Posts
  useEffect(() => {
    const handleViewCountUpdate = (event) => {
      const postId = String(event?.detail?.postId || '');
      const nextViewCount = Number(event?.detail?.viewCount);

      if (!postId || !Number.isFinite(nextViewCount)) return;

      const safeViewCount = Math.max(0, nextViewCount);

      setDisplayedPosts((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          return prev;
        }

        let changed = false;

        const nextPosts = prev.map((post) => {
          if (!post) return post;

          let nextPost = post;
          const currentPostId = String(post._id || post.id || '');

          if (
            currentPostId === postId &&
            Number(post.viewCount || 0) !== safeViewCount
          ) {
            nextPost = {
              ...nextPost,
              viewCount: safeViewCount,
            };

            changed = true;
          }

          const original = nextPost.repostOf;

          if (original && typeof original === 'object') {
            const originalId = String(
              original._id || original.id || ''
            );

            if (
              originalId === postId &&
              Number(original.viewCount || 0) !== safeViewCount
            ) {
              nextPost = {
                ...nextPost,
                repostOf: {
                  ...original,
                  viewCount: safeViewCount,
                },
              };

              changed = true;
            }
          }

          return nextPost;
        });

        return changed ? nextPosts : prev;
      });
    };

    window.addEventListener(
      POST_VIEW_COUNT_UPDATED_EVENT,
      handleViewCountUpdate
    );

    return () => {
      window.removeEventListener(
        POST_VIEW_COUNT_UPDATED_EVENT,
        handleViewCountUpdate
      );
    };
  }, []);

  const loadNextBatch = useCallback(async () => {
    if (!currentUser?.username) {
      return;
    }

    if (!Array.isArray(savedPostIds)) {
      return;
    }

    if (loading || currentIndex >= savedPostIds.length) {
      return;
    }

    const nextIds = savedPostIds.slice(
      currentIndex,
      currentIndex + BATCH_SIZE
    );

    if (nextIds.length === 0) {
      setHasMore(false);
      return;
    }

    setLoading(true);

    try {
      console.log(
        '📦 Fetching saved post batch:',
        nextIds
      );

      const response = await api.post('/posts/batch', {
        ids: nextIds,
        username: currentUser.username,
      });

      const posts = Array.isArray(response?.data?.posts)
        ? response.data.posts
        : [];

      const enriched = await Promise.all(
        posts.map(async (post) => {

          const enrichedPost = {
            ...post,
            viewCount: Math.max(0, Number(post.viewCount) || 0),
          };

          /* ===============================================
             Preserve paid/selected post background styling
          =============================================== */

          enrichedPost.style =
            post.style || null;

          enrichedPost.backgroundTheme =
            post.backgroundTheme ||
            post.style?.backgroundTheme ||
            'normal';

          enrichedPost.hasPaidStyle =
            post.hasPaidStyle === true ||
            post.style?.paid === true ||
            enrichedPost.backgroundTheme !== 'normal';

          /* ===============================================
             Preserve repost information
          =============================================== */

          enrichedPost.isRepost =
            post.isRepost === true ||
            post.type === 'repost';

          enrichedPost.repostOf =
            post.repostOf || null;

          enrichedPost.repostComment =
            post.repostComment || '';

          /* ===============================================
             Preserve product repost information
          =============================================== */

          enrichedPost.postType =
            post.postType || 'post';

          enrichedPost.sharedProduct =
            post.sharedProduct || null;

          enrichedPost.productSnapshot =
            post.productSnapshot || null;

          /* ===============================================
             Preserve optional post destination action
          =============================================== */

          enrichedPost.postAction =
            post.postAction || null;

          /* ===============================================
             Preserve post state and metadata
          =============================================== */

          enrichedPost.pinned =
            post.pinned === true;

          enrichedPost.stakeActivePi =
            Number(post.stakeActivePi || 0);

          enrichedPost.stakeNextExpiryAt =
            post.stakeNextExpiryAt || null;

          enrichedPost.badgesBrief =
            Array.isArray(post.badgesBrief)
              ? post.badgesBrief
              : [];

          /* ===============================================
             Load live interaction information
          =============================================== */

          try {
            const interactionData =
              await getPostInteractions(
                post._id,
                true
              );

            enrichedPost.likes =
              Array.isArray(interactionData?.likes)
                ? interactionData.likes
                : [];

            enrichedPost.comments =
              Array.isArray(interactionData?.comments)
                ? interactionData.comments
                : [];

            enrichedPost.likesCount =
              enrichedPost.likes.length;

            enrichedPost.commentsCount =
              enrichedPost.comments.length;

            enrichedPost.userHasLiked =
              enrichedPost.likes.some(
                (item) =>
                  String(
                    item?.username || ''
                  ).toLowerCase() ===
                  String(
                    currentUser.username
                  ).toLowerCase()
              );
          } catch (error) {
            console.error(
              `❌ Failed to load interactions for saved post ${post._id}:`,
              error
            );

            enrichedPost.likes = [];
            enrichedPost.comments = [];
            enrichedPost.likesCount =
              Number(post.likesCount || 0);

            enrichedPost.commentsCount =
              Number(post.commentsCount || 0);

            enrichedPost.userHasLiked =
              post.userHasLiked === true;
          }

          enrichedPost.timestamp =
            post.createdAt ||
            post.timestamp ||
            null;

          /*
           * Post.js receives repost status separately through isRepost.
           * Keep the normal rendered item type stable here.
           */
          enrichedPost.type = 'post';

          /* ===============================================
             Preserve complete author information
          =============================================== */

          enrichedPost.user = post.user || {
            name: post.name,
            username: post.username,

            profilePic:
              post.profilePic ||
              placeholderProfile,

            userType: post.userType,

            isMerchant:
              post.isMerchant === true,

            merchantActive:
              post.merchantActive === true,

            isAdmin:
              post.isAdmin === true,

            adminRole:
              post.adminRole || null,

            isApp:
              post.isApp === true,

            accountType:
              post.accountType || 'primary',

            badgesBrief:
              Array.isArray(post.badgesBrief)
                ? post.badgesBrief
                : [],

            location:
              post.location || '',
          };

          return enrichedPost;
        })
      );

      setDisplayedPosts((previousPosts) => {
        const existingIds = new Set(
          previousPosts.map((post) =>
            String(post?._id || '')
          )
        );

        const newUniquePosts =
          enriched.filter((post) => {
            const postId = String(
              post?._id || ''
            );

            return (
              postId &&
              !existingIds.has(postId)
            );
          });

        return [
          ...previousPosts,
          ...newUniquePosts,
        ];
      });

      const nextIndex =
        currentIndex + nextIds.length;

      setCurrentIndex(nextIndex);

      setHasMore(
        nextIndex < savedPostIds.length
      );
    } catch (error) {
      console.error(
        '❌ Error loading saved posts batch:',
        error
      );
    } finally {
      setLoading(false);
    }
  }, [
    currentIndex,
    currentUser?.username,
    getPostInteractions,
    loading,
    savedPostIds,
  ]);

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    if (
      !hasLoaded ||
      !currentUser?.username
    ) {
      return;
    }

    if (
      Array.isArray(savedPostIds) &&
      savedPostIds.length === 0
    ) {
      setHasMore(false);
      return;
    }

    loadNextBatch();
  }, [
    currentUser?.username,
    hasLoaded,
    loadNextBatch,
    savedPostIds,
  ]);

  /* =======================================================
     INFINITE SCROLL
  ======================================================= */

  useEffect(() => {
    const scrollArea = feedRef.current;

    if (!scrollArea) {
      return undefined;
    }

    const handleScroll = () => {
      if (loading || !hasMore) {
        return;
      }

      const {
        scrollTop,
        scrollHeight,
        clientHeight,
      } = scrollArea;

      const nearBottom =
        scrollTop + clientHeight >=
        scrollHeight - 200;

      if (nearBottom) {
        loadNextBatch();
      }
    };

    scrollArea.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      scrollArea.removeEventListener(
        'scroll',
        handleScroll
      );
    };
  }, [
    hasMore,
    loadNextBatch,
    loading,
  ]);

  return (
    <div className="saved-screen-container">
      <div className="saved-topbar">
        <button
          type="button"
          className="saved-back-btn"
          onClick={() => window.history.back()}
          aria-label="Go back"
        >
          <img
            src={backIcon}
            alt=""
            className="saved-back-icon"
          />
        </button>

        <h2 className="saved-title">
          Saved Posts
        </h2>

        <div
          className="saved-topbar-spacer"
          aria-hidden="true"
        />
      </div>

      <div
        className="saved-scroll-area saved-post-feed"
        ref={feedRef}
      >
        {displayedPosts.map((post) => {
          if (!post?._id) {
            return null;
          }

          return (
            <Post
              key={post._id}
              {...post}
              id={post._id}
              poll={post.poll || null}

currentUser={currentUser.username}
currentUserProfilePic={currentUser?.profilePic}
currentScreen="saved"

              wrapperId={`post-${post._id}`}
              isSaved={true}

              /* Paid post background */
              style={post.style || null}
              backgroundTheme={
                post.backgroundTheme ||
                post.style?.backgroundTheme ||
                'normal'
              }
              hasPaidStyle={
                post.hasPaidStyle === true ||
                post.style?.paid === true
              }

              /* Reposts */
              isRepost={
                post.isRepost === true
              }
              repostOf={
                post.repostOf || null
              }
              repostComment={
                post.repostComment || ''
              }

              /* Product reposts */
              postType={
                post.postType || 'post'
              }
              sharedProduct={
                post.sharedProduct || null
              }
              productSnapshot={
                post.productSnapshot || null
              }

              /* Optional post CTA */
              postAction={
                post.postAction || null
              }

              /* Stake information */
              pinned={
                post.pinned === true
              }
              stakeActivePi={
                Number(
                  post.stakeActivePi || 0
                )
              }
              stakeNextExpiryAt={
                post.stakeNextExpiryAt ||
                null
              }
            />
          );
        })}

        {loading && (
          <div
            className="saved-spinner"
            aria-label="Loading saved posts"
          />
        )}

        {!loading &&
          displayedPosts.length === 0 && (
            <p className="no-saved-msg">
              You haven't saved any posts yet.
            </p>
          )}

        {!hasMore &&
          displayedPosts.length > 0 && (
            <div className="saved-end-message">
              No more saved posts
            </div>
          )}
      </div>
    </div>
  );
}

export default SavedPosts;
